import { AdminHeader } from "@/components/AdminHeader";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Award } from "lucide-react";

const TIER_STYLES = {
  "Platinum Islander": "text-[#C71E2E] border-[#C71E2E]/30 bg-[#C71E2E]/10",
  "Gold Voyager": "text-amber-700 border-amber-200 bg-amber-50",
  "Silver Explorer": "text-slate-600 border-slate-300 bg-slate-50",
  "Island Guest": "text-muted-foreground border-[#E5DED5] bg-[#FAF6F0]",
};

const ROW = "border-[#E5DED5] hover:bg-[#F5EFE6]/50 transition-colors";

export default function LoyaltyTab() {
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/admin/loyalty-members").then((r) => setMembers(r.data)).catch(() => {});
  }, []);

  const filtered = members.filter((m) => {
    const q = search.trim().toLowerCase();
    return !q || [m.name, m.email, m.tier].some((v) => (v || "").toLowerCase().includes(q));
  });

  return (
    <div data-testid="admin-loyalty-tab">
      <AdminHeader testid="admin-header-loyalty" eyebrow="Marketing" title="Loyalty Members" description="Tiers, discounts and progress for returning renters." actions={<div className="w-full sm:w-72"><Input data-testid="loyalty-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email or tier…" className="input-dark" /></div>} />
      <div className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-[#E5DED5] hover:bg-transparent">
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Tier</TableHead>
              <TableHead>Discount</TableHead>
              <TableHead>Rentals</TableHead>
              <TableHead>Next Tier</TableHead>
              <TableHead>Joined</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow className={ROW}>
                <TableCell colSpan={7} data-testid="loyalty-empty" className="text-muted-foreground font-ui py-10 text-center">
                  No loyalty members yet — signups from the homepage will appear here.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((m) => (
              <TableRow key={m.id} data-testid={`loyalty-row-${m.id}`} className={ROW}>
                <TableCell className="font-ui font-medium whitespace-nowrap">{m.name}</TableCell>
                <TableCell className="text-muted-foreground">{m.email}</TableCell>
                <TableCell>
                  <span data-testid={`loyalty-tier-badge-${m.id}`} className={`inline-flex items-center gap-1.5 text-[10px] font-ui tracking-[0.15em] uppercase border rounded-full px-3 py-1 whitespace-nowrap ${TIER_STYLES[m.tier] || TIER_STYLES["Island Guest"]}`}>
                    <Award size={11} /> {m.tier}
                  </span>
                </TableCell>
                <TableCell className="text-[#C71E2E] font-ui font-semibold">{m.percent ? `−${m.percent}%` : "—"}</TableCell>
                <TableCell className="font-ui">{m.rentals}</TableCell>
                <TableCell data-testid={`loyalty-progress-${m.id}`} className="text-muted-foreground text-xs font-ui whitespace-nowrap">
                  {m.next_tier ? (
                    <span className="block">
                      {m.rentals_to_next} more → {m.next_tier}
                      <span className="mt-1.5 block h-1.5 w-24 rounded-full bg-[#E5DED5] overflow-hidden">
                        <span className="block h-full rounded-full bg-[#C71E2E]" style={{ width: `${Math.min(100, (m.rentals / (m.rentals + m.rentals_to_next)) * 100)}%` }} />
                      </span>
                    </span>
                  ) : "Top tier"}
                </TableCell>
                <TableCell className="text-muted-foreground whitespace-nowrap">
                  {m.created_at ? new Date(m.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p data-testid="loyalty-count" className="mt-3 text-xs text-muted-foreground font-ui">
        {members.length} loyalty {members.length === 1 ? "member" : "members"}
      </p>
    </div>
  );
}
